import { RankedLeaderboardEntry } from "@/src/models/leaderboard.model";
import { useTheme } from "@/src/styles/theme-provider";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";

type LeaderboardRowProps = Readonly<{
  entry: RankedLeaderboardEntry;
  isSelected: boolean;
  isLast?: boolean;
  onPress: () => void;
}>;

type RankRowProps = Readonly<{
  onPress: () => void;
  isSelected: boolean;
  isLast?: boolean;
  children: React.ReactNode;
}>;

type RowContentProps = Readonly<{
  entry: RankedLeaderboardEntry;
}>;

type RankBadgeProps = Readonly<{
  rank: number;
}>;

const RANK_BADGE_BG: Readonly<Record<number, string>> = {
  1: "bg-wheat",
  2: "bg-coral",
  3: "bg-ash-3",
};

const RANK_AVATAR_RING_COLOR: Readonly<Record<number, string>> = {
  1: "border-primary",
  2: "border-primary-1",
  3: "border-primary-1",
};

const Rank1Row = ({ onPress, isSelected, isLast, children }: RankRowProps) => (
  <Pressable
    onPress={onPress}
    className={`flex-row items-center px-4 py-3 hover:bg-ash-3 ${
      isLast ? "" : "border-b border-ash-3"
    } ${isSelected ? "bg-wheat" : "bg-wheat/50"}`}
  >
    {children}
  </Pressable>
);

const Rank2Row = ({ onPress, isSelected, isLast, children }: RankRowProps) => (
  <Pressable
    onPress={onPress}
    className={`flex-row items-center px-4 py-3 hover:bg-ash-3 ${
      isLast ? "" : "border-b border-ash-3"
    } ${isSelected ? "bg-coral" : "bg-coral/50"}`}
  >
    {children}
  </Pressable>
);

const Rank3Row = ({ onPress, isSelected, isLast, children }: RankRowProps) => (
  <Pressable
    onPress={onPress}
    className={`flex-row items-center px-4 py-3 hover:bg-ash-3 ${
      isLast ? "" : "border-b border-ash-3"
    } ${isSelected ? "bg-ash-3" : "bg-ash"}`}
  >
    {children}
  </Pressable>
);

export const LeaderboardTableHeader = () => {
  const { t } = useTranslation();
  return (
    <View className="flex-row items-center px-4 py-2 border-b border-ash-3 bg-primary-2">
      <Text className="w-10 font-semibold text-center tracking-widest text-xs text-primary">
        #
      </Text>
      <Text className="flex-1 font-semibold tracking-widest pl-2 text-xs text-primary">
        {t("LABEL_COL_NAME")}
      </Text>
      <Text className="w-24 font-semibold tracking-widest text-center text-xs text-primary">
        {t("LABEL_COL_POINTS")}
      </Text>
      <Text className="w-12 font-black tracking-widest text-center text-xs">
        🏅
      </Text>
    </View>
  );
};

const RankBadge = ({ rank }: RankBadgeProps) => {
  const bgClass = RANK_BADGE_BG[rank];
  if (!bgClass) {
    return <Text className="font-bold text-xs text-slate">{rank}</Text>;
  }
  return (
    <View
      className={`w-6 h-6 rounded-full items-center justify-center ${bgClass}`}
    >
      <Text className="text-onyx text-xs font-semibold">{rank}</Text>
    </View>
  );
};

const LeaderboardRowContent = ({ entry }: RowContentProps) => {
  const isTopThree = entry?.rank <= 3;
  const formattedPoints = entry?.points?.toLocaleString() ?? "0";
  const achievementsCount = entry?.achievements ?? 0;
  const ringClass = RANK_AVATAR_RING_COLOR[entry?.rank];
  const avatarRingClassName =
    isTopThree && ringClass
      ? `rounded-full p-0.5 border-2 ${ringClass}`
      : undefined;

  return (
    <>
      <View className="w-10 items-center">
        <RankBadge rank={entry?.rank} />
      </View>

      <View className="flex-1 flex-row items-center gap-2 min-w-0">
        <View className={avatarRingClassName}>
          <BlocksAvatar
            userId={entry?.userId}
            fallbackImageUrl={entry?.avatar}
            name={entry?.displayName}
            size={32}
          />
        </View>
        <Text
          className="flex-1 font-medium text-xs text-onyx"
          numberOfLines={1}
        >
          {entry?.displayName}
        </Text>
      </View>

      <Text className="w-24 font-semibold text-center text-xs text-slate">
        {formattedPoints}
      </Text>

      <Text className="w-12 font-black text-center text-xs text-primary">
        {achievementsCount}
      </Text>
    </>
  );
};

const LeaderboardRow = ({
  entry,
  isSelected,
  isLast,
  onPress,
}: LeaderboardRowProps) => {
  const { themeVars } = useTheme();

  if (entry?.rank === 1)
    return (
      <Rank1Row onPress={onPress} isSelected={isSelected} isLast={isLast}>
        <LeaderboardRowContent entry={entry} />
      </Rank1Row>
    );
  if (entry?.rank === 2)
    return (
      <Rank2Row onPress={onPress} isSelected={isSelected} isLast={isLast}>
        <LeaderboardRowContent entry={entry} />
      </Rank2Row>
    );
  if (entry?.rank === 3)
    return (
      <Rank3Row onPress={onPress} isSelected={isSelected} isLast={isLast}>
        <LeaderboardRowContent entry={entry} />
      </Rank3Row>
    );

  return (
    <Pressable
      onPress={onPress}
      className={`flex-row items-center px-4 py-3 hover:bg-ash-3 ${
        isLast ? "" : "border-b border-ash-3"
      }`}
      style={
        isSelected
          ? { backgroundColor: themeVars["--color-primary-2"] as string }
          : undefined
      }
    >
      <LeaderboardRowContent entry={entry} />
    </Pressable>
  );
};

export default LeaderboardRow;
